import { Plus } from "lucide-react";
import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { Field } from "@/components/Field";
import { STATUS_CLASSES, StatusPill } from "@/components/StatusPill";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Banner } from "@/components/ui/banner";
import { Breadcrumb } from "@/components/ui/breadcrumb";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { SegmentedControl } from "@/components/ui/segmented-control";
import { Select } from "@/components/ui/select";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { Stepper } from "@/components/ui/stepper";
import { Table, TBody, TD, TH, THead, TR } from "@/components/ui/table";
import { NavTabs } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "@/components/ui/toast";
import { Tooltip } from "@/components/ui/tooltip";
import tokensCss from "@/design/tokens.css?raw";
import { CONTRAST_PAIRS, contrastRatio, parseTokens, resolveToken } from "@/design/tokens";
import type { ProjectStatus } from "@/api/types";

const tokens = parseTokens(tokensCss);

function safeResolve(name: string): string {
  try {
    return resolveToken(name, tokens);
  } catch {
    return "";
  }
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-4">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function Swatch({ name }: { name: string }) {
  return (
    <li className="flex items-center gap-3 rounded-md border border-border bg-surface p-2">
      <span aria-hidden="true" className="h-10 w-10 shrink-0 rounded-md border border-border" style={{ background: `var(${name})` }} />
      <span className="flex min-w-0 flex-col">
        <code className="truncate font-mono text-sm text-fg">{name}</code>
        <span className="font-mono text-xs text-fg-muted">{safeResolve(name)}</span>
      </span>
    </li>
  );
}

/** Staff-only living style guide: every token and component state. */
export function DesignSystemPage() {
  const { t } = useTranslation();
  const [segment, setSegment] = useState<"all" | "open" | "done">("all");
  const [step, setStep] = useState("two");
  const names = Object.keys(tokens);
  const semantic = names.filter((n) => n.startsWith("--color-") && !n.startsWith("--color-status-"));
  const primitives = names.filter((n) => n.startsWith("--cm-"));
  const s = (k: string) => t(`designSystem.sample.${k}`);

  return (
    <div className="flex flex-col gap-12">
      <PageHeader title={t("designSystem.title")} description={t("designSystem.intro")} />

      <Section title={t("designSystem.sections.colours")}>
        <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {semantic.map((n) => (
            <Swatch key={n} name={n} />
          ))}
        </ul>
      </Section>

      <Section title={t("designSystem.sections.primitives")}>
        <ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {primitives.map((n) => (
            <Swatch key={n} name={n} />
          ))}
        </ul>
      </Section>

      <Section title={t("designSystem.sections.status")}>
        <div className="flex flex-wrap gap-3">
          {(Object.keys(STATUS_CLASSES) as ProjectStatus[]).map((st) => (
            <StatusPill key={st} status={st} />
          ))}
        </div>
      </Section>

      <Section title={t("designSystem.sections.type")}>
        <Card>
          <CardContent className="flex flex-col gap-3 pt-6">
            <h1>{s("heading1")}</h1>
            <h2>{s("heading2")}</h2>
            <h3>{s("heading3")}</h3>
            <p>{s("body")}</p>
            <p className="text-fg-muted">{s("muted")}</p>
            <a href="#top">{s("link")}</a>
          </CardContent>
        </Card>
      </Section>

      <Section title={t("designSystem.sections.buttons")}>
        <div className="flex flex-wrap items-center gap-3">
          <Button>{s("primary")}</Button>
          <Button variant="secondary">{s("secondary")}</Button>
          <Button variant="solid">{s("solid")}</Button>
          <Button variant="danger">{s("danger")}</Button>
          <Button variant="ghost">{s("ghost")}</Button>
          <Button variant="link">{s("link")}</Button>
          <Button disabled>{s("disabled")}</Button>
          <Button size="sm" variant="secondary">
            {s("small")}
          </Button>
          <Button size="lg">{s("large")}</Button>
          <Button size="icon" variant="secondary" aria-label={s("icon")}>
            <Plus aria-hidden="true" className="h-5 w-5" />
          </Button>
        </div>
      </Section>

      <Section title={t("designSystem.sections.badges")}>
        <div className="flex flex-wrap items-center gap-3">
          {(["default", "solid", "outline", "grey", "success", "warning", "danger", "info"] as const).map((v) => (
            <Badge key={v} variant={v}>
              {t(`designSystem.badge.${v}`)}
            </Badge>
          ))}
          <Avatar name={s("person")} size="sm" />
          <Avatar name={s("person")} />
          <Avatar name={s("person")} size="lg" />
        </div>
      </Section>

      <Section title={t("designSystem.sections.forms")}>
        <div className="grid gap-6 md:grid-cols-2">
          <Field label={s("inputLabel")} hint={s("inputHint")}>
            {(p) => <Input {...p} defaultValue={s("rowOne")} />}
          </Field>
          <Field label={s("inputLabel")} error={s("inputError")}>
            {(p) => <Input {...p} />}
          </Field>
          <Field label={s("selectLabel")}>
            {(p) => (
              <Select {...p} defaultValue="website">
                <option value="website">{t("project.type.website")}</option>
                <option value="mobile_app">{t("project.type.mobile_app")}</option>
              </Select>
            )}
          </Field>
          <Field label={s("disabled")}>{(p) => <Input {...p} disabled defaultValue={s("disabled")} />}</Field>
          <Field label={s("textareaLabel")} className="md:col-span-2">
            {(p) => <Textarea {...p} />}
          </Field>
        </div>
        <SegmentedControl
          label={s("segmentAll")}
          value={segment}
          onChange={setSegment}
          options={[
            { value: "all", label: s("segmentAll") },
            { value: "open", label: s("segmentOpen") },
            { value: "done", label: s("segmentDone") },
          ]}
        />
      </Section>

      <Section title={t("designSystem.sections.feedback")}>
        <div className="flex flex-col gap-3">
          <Banner tone="info">{s("info")}</Banner>
          <Banner tone="success">{s("success")}</Banner>
          <Banner tone="warning">{s("warning")}</Banner>
          <Banner tone="danger">{s("danger")}</Banner>
          <Banner tone="brand" title={s("cardTitle")}>
            {s("cardBody")}
          </Banner>
          <div>
            <Button variant="secondary" onClick={() => toast.success(s("toastText"))}>
              {s("toast")}
            </Button>
          </div>
        </div>
      </Section>

      <Section title={t("designSystem.sections.navigation")}>
        <Breadcrumb
          label={t("shell.breadcrumb")}
          items={[
            { label: s("crumbOrg"), to: "/design-system" },
            { label: s("crumbProject"), to: "/design-system" },
            { label: s("crumbPage") },
          ]}
        />
        <NavTabs
          label={s("tabOne")}
          tabs={[
            { to: "/design-system", label: s("tabOne"), end: true },
            { to: "/design-system/q", label: s("tabTwo"), count: 3 },
            { to: "/design-system/f", label: s("tabThree") },
          ]}
        />
        <Stepper
          label={s("stepOne")}
          current={step}
          onSelect={setStep}
          steps={[
            { id: "one", label: s("stepOne") },
            { id: "two", label: s("stepTwo") },
            { id: "three", label: s("stepThree") },
            { id: "four", label: s("stepFour") },
          ]}
        />
      </Section>

      <Section title={t("designSystem.sections.data")}>
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <h3>{s("cardTitle")}</h3>
            </CardHeader>
            <CardContent>{s("cardBody")}</CardContent>
          </Card>
          <Card elevation="flat" className="flex flex-col gap-3 p-6">
            <Skeleton className="h-6 w-1/2" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
          </Card>
        </div>
        <Table>
          <THead>
            <TR>
              <TH>{s("tableName")}</TH>
              <TH>{s("tableStatus")}</TH>
            </TR>
          </THead>
          <TBody>
            <TR>
              <TD>{s("rowOne")}</TD>
              <TD>
                <StatusPill status="requested" />
              </TD>
            </TR>
            <TR>
              <TD>{s("rowTwo")}</TD>
              <TD>
                <StatusPill status="build" />
              </TD>
            </TR>
          </TBody>
        </Table>
        <EmptyState
          title={s("empty")}
          description={s("emptyBody")}
          icon={<Plus className="h-6 w-6" />}
          action={<Button>{s("primary")}</Button>}
        />
      </Section>

      <Section title={t("designSystem.sections.overlays")}>
        <div className="flex flex-wrap gap-3">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary">{s("menu")}</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              <DropdownMenuItem>{s("menuOne")}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem>{s("menuTwo")}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="secondary">{s("sheet")}</Button>
            </SheetTrigger>
            <SheetContent>
              <SheetTitle className="font-heading text-xl font-semibold text-heading">{s("sheetTitle")}</SheetTitle>
              <SheetDescription>{s("sheetBody")}</SheetDescription>
            </SheetContent>
          </Sheet>
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="secondary">{s("dialog")}</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{s("dialogTitle")}</DialogTitle>
                <DialogDescription>{s("dialogBody")}</DialogDescription>
              </DialogHeader>
            </DialogContent>
          </Dialog>
          <Tooltip content={s("tooltipText")}>
            <Button variant="ghost">{s("tooltip")}</Button>
          </Tooltip>
        </div>
      </Section>

      <Section title={t("designSystem.sections.contrast")}>
        <Table>
          <THead>
            <TR>
              <TH>{t("designSystem.use")}</TH>
              <TH>{t("designSystem.token")}</TH>
              <TH>{t("designSystem.ratio")}</TH>
            </TR>
          </THead>
          <TBody>
            {CONTRAST_PAIRS.map((p) => {
              const fg = safeResolve(p.fg);
              const bg = safeResolve(p.bg);
              const ratio = fg && bg ? contrastRatio(fg, bg) : 0;
              return (
                <TR key={`${p.fg}-${p.bg}`}>
                  <TD>
                    <span className="inline-flex rounded-md px-2 py-1" style={{ color: `var(${p.fg})`, background: `var(${p.bg})` }}>
                      {p.use}
                    </span>
                  </TD>
                  <TD className="font-mono text-sm">
                    {p.fg} / {p.bg}
                  </TD>
                  <TD>
                    <Badge variant={ratio >= p.min ? "success" : "danger"}>
                      {ratio.toFixed(2)} ({p.min})
                    </Badge>
                  </TD>
                </TR>
              );
            })}
          </TBody>
        </Table>
      </Section>
    </div>
  );
}
